<template>
  <v-dialog v-model="visible" max-width="638" persistent>
    <v-card v-if="visible" class="v-dialog--custom">
      <v-card-title class="headline pa-4">
        <span class="v-dialog--custom__title">
          {{
            type === 'create'
              ? $t(`${namespace}.reverseRide`)
              : $t(`${namespace}.editReverseRide`)
          }} #{{ data.receipt.corporateJob.apiInfo.apiTaxiJobId }}
        </span>
        <v-btn
          class="v-dialog--custom__close mr-2"
          color="gray-6"
          flat
          fab
          :disabled="isLoading"
          @click.native="close(null)"
        >
          <v-icon dark>close</v-icon>
        </v-btn>
      </v-card-title>

      <form class="form--custom" novalidate @submit.prevent="handleSubmit">
        <v-card-text v-if="isValidObject(data)" class="px-4 py-0">
          <v-radio-group
            v-model="radioGroup"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t(`${namespace}.fieldChargebackName`))"
            :data-vv-name="$t(`${namespace}.fieldChargebackName`)"
            required
            class="mt-0"
          >
            <v-radio :label="$t(`${namespace}.totalChargeback`)" value="totalChargeback"></v-radio>
            <v-radio :label="$t(`${namespace}.partialChargeback`)" value="partialChargeback"></v-radio>
          </v-radio-group>

          <div v-if="radioGroup === 'partialChargeback'" style="padding-left: 32px;">
            <label>{{ $t(`${namespace}.fieldDiscountAmountLabel`) }}</label>
            <v-text-field
              v-model="chargebackValue"
              v-money="money"
              ref="chargebackValue"
              :placeholder="$t(`${namespace}.fieldDiscountAmountPlaceholder`)"
              :error-messages="errors.collect($t(`${namespace}.fieldDiscountAmount`))"
              v-validate="{
                required: true,
                isValueAllowed: [0, data.amount]
              }"
              :data-vv-name="$t(`${namespace}.fieldDiscountAmount`)"
              required
              flat
              outline
              solo
              color="accent"
            />
          </div>

          <label>{{ $t(`${namespace}.justification`) }}</label>
          <v-select
            v-model="reason"
            item-text="text"
            :placeholder="$t(`${namespace}.chooseAnOption`)"
            :items="objectToArray($t(`${namespace}.reasonList`))"
            :error-messages="errors.collect($t(`${namespace}.justificationName`))"
            v-validate="{ required: true }"
            :data-vv-name="$t(`${namespace}.justificationName`)"
            noDataText=""
            required
            flat
            outline
            solo
            color="accent"
          />

          <div v-if="reason === 'other'">
            <label>{{ $t(`${namespace}.fieldReasonTextLabel`) }}</label>
            <v-text-field
              v-model="reasonText"
              flat
              outline
              solo
              color="accent"
              clearable
              :name="$t(`${namespace}.fieldReasonText`)"
              :placeholder="$t(`${namespace}.fieldReasonTextPlaceholder`)"
              v-validate="{ required: true }"
              :error-messages="errors.collect($t(`${namespace}.fieldReasonText`))"
              :data-vv-name="$t(`${namespace}.fieldReasonText`)"
              required
            />
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer/>

          <a
            v-if="!isLoading"
            href="#"
            class="text--normal accent--text mr-4"
            color="accent"
            flat
            round
            @click.prevent="close(null)"
          >
            {{ $t('Common.cancel') }}
          </a>

          <v-btn
            type="submit"
            :loading="isLoading"
            :disabled="isLoading"
            color="accent"
            depressed
            round
          >
            {{
              type === 'create'
                ? $t(`${namespace}.applyChargeback`)
                : $t('Common.saveChanges')
            }}
          </v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import { VMoney } from 'v-money'
import { isObject, isArray, stringToFloat, objectToArray, convertToCoin } from 'utils/functions'

export default {
  name: 'ReverseRideDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'create'
    },
    data: {
      type: Object
    }
  },
  directives: {
    money: VMoney
  },
  data () {
    return {
      namespace: 'Views.FinancialAdjustmentsPage',
      isLoading: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },
      radioGroup: null,
      chargebackValue: null,
      value: null,
      reason: null,
      reasonText: null
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidObject (val) {
      return isObject(val)
    },

    isValidArray (val) {
      return isArray(val)
    },

    objectToArray: (val) => objectToArray(val),

    convertToMoney () {
      this.value = stringToFloat(this.chargebackValue)
    },

    resetAllFields () {
      this.radioGroup = null
      this.value = null
      this.reason = null
      this.reasonText = null
    },

    close (reload = null) {
      this.resetAllFields()
      this.$emit('close', reload)
    },

    renderPayload () {
      // chargeback type
      this.radioGroup = this.data.chargeBack.value < this.data.amount
        ? 'partialChargeback'
        : 'totalChargeback'

      // reason
      const hasReasonList = this.objectToArray(this.$t(`${this.namespace}.reasonList`))
        .find((reason) => reason.text === this.data.chargeBack.reason)

      if (hasReasonList) {
        this.reason = this.data.chargeBack.reason
        this.reasonText = null
      } else {
        this.reason = 'other'
        this.reasonText = this.data.chargeBack.reason
      }

      // chargeback value
      this.chargebackValue = convertToCoin(this.data.chargeBack.value)
      const $chargebackValue = this.$refs['chargebackValue']
      if ($chargebackValue) {
        $chargebackValue.$el.querySelector('input').value = convertToCoin(this.data.chargeBack.value)
      }
    },

    async addChargeback () {
      this.isLoading = true

      try {
        const { serviceReceiptId, amount } = this.data
        const value = this.radioGroup === 'totalChargeback' ? amount : this.value
        const reason = this.reason === 'other' ? this.reasonText : this.reason

        const { data: res } = await this.$apiService.createChargeBack({
          serviceReceiptId,
          value,
          reason
        })

        if (!res.valid) {
          const error = this.isValidArray(res.reasons) ? res.reasons[0] : res.message
          this.showAlert({
            type: 'error',
            message: error || this.$t(`${this.namespace}.chargebackRideError`)
          })
        } else {
          const message = this.radioGroup === 'totalChargeback'
            ? this.$t(`${this.namespace}.totalChargebackRideSuccess`)
            : this.$t(`${this.namespace}.partialChargebackRideSuccess`).replace('[CHARGEBACK_VALUE]', convertToCoin(this.chargebackValue))

          this.close(true)
          this.showAlert({ type: 'success', message })
        }
      } catch (err) {
        console.log('ERROR:', err)
        this.showAlert({
          type: 'error',
          message: err || this.$t(`${this.namespace}.chargebackRideError`)
        })
      }

      this.isLoading = false
    },

    async editChargeback () {
      this.isLoading = true

      try {
        const { amount } = this.data
        const value = this.radioGroup === 'totalChargeback' ? amount : this.value
        const reason = this.reason === 'other' ? this.reasonText : this.reason
        const chargeBackId = this.data.chargeBack.id

        const { data: res } = await this.$apiService.updateChargeBack(chargeBackId, {
          reason,
          value
        })

        if (!res.valid) {
          const error = this.isValidArray(res.reasons) ? res.reasons[0] : res.message
          this.showAlert({
            type: 'error',
            message: error || this.$t(`${this.namespace}.chargebackRideError`)
          })
        } else {
          const message = this.radioGroup === 'totalChargeback'
            ? this.$t(`${this.namespace}.editTotalChargebackRideSuccess`)
            : this.$t(`${this.namespace}.editPartialChargebackRideSuccess`).replace('[CHARGEBACK_VALUE]', convertToCoin(this.chargebackValue))

          this.close(true)
          this.showAlert({ type: 'success', message })
        }
      } catch (err) {
        console.log('ERROR:', err)
        this.showAlert({
          type: 'error',
          message: err || this.$t(`${this.namespace}.chargebackRideError`)
        })
      }

      this.isLoading = false
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()
        if (isValid) {
          this.convertToMoney()

          if (this.type === 'create') {
            this.addChargeback()
          } else {
            this.editChargeback()
          }
        }
      } catch (err) {
        console.log('ERROR:', err)
      }
    }
  },
  watch: {
    visible (val) {
      // fix validation error when close dialog
      this.$validator._paused = !val
    },

    radioGroup (val) {
      if (val === 'totalChargeback') {
        this.value = this.data.amount
        this.chargebackValue = null

        if (this.$refs['chargebackValue']) {
          this.$refs['chargebackValue'].$el.querySelector('input').value = 0
        }
      }
    },

    reason (val) {
      if (val !== 'other') {
        this.reasonText = null
      }
    },

    data (val) {
      if (this.type === 'edit' && this.isValidObject(val) && val.chargeBack) {
        setTimeout(() => { this.renderPayload() }, 500)
      }
    }
  },
  created () {
    this.$validator.extend('isValueAllowed', {
      getMessage: (field, params, data) => {
        const message = this.$t(`${this.namespace}.minAndMaxValueValidationMessage`)
          .replace('[MIN_VALUE]', '{1}')
          .replace('[MAX_VALUE]', '{2}')
        return message
      },
      validate (value, params) {
        const [min = 0, max = 0] = params
        return stringToFloat(value) > Number(min) && stringToFloat(value) <= Number(max)
      }
    })
  }
}
</script>
